import React, { useState } from "react";
import moment from "moment";
import queries from "../helpers/queries";
import { useQuery } from "@apollo/react-hooks";
import { withRouter } from "react-router-dom";
import { useLocation, useHistory } from "react-router-dom";
import { FaChevronDown, FaChevronUp, FaArrowLeft } from "react-icons/fa";
import {
    FiMapPin,
    FiBriefcase,
    FiFileText,
    FiMaximize2,
    FiCalendar,
    FiUsers,
    FiTrendingUp,
    FiClipboard,
    FiUser,
    FiInfo,
    FiLayers,
    FiGrid,
    FiHome,
    FiGift,
    FiCheck,
    FiPercent,
    FiMessageSquare,
} from "react-icons/fi";
import "../override.css";

const DIRECTOR_DATE_FORMATS = ["DD/MM/YYYY", "YYYY-MM-DD"];

const parseDirectorDate = (value) => {
    if (!value) return null;
    const parsed = moment(value, DIRECTOR_DATE_FORMATS, true);
    return parsed.isValid() ? parsed : null;
};

const capitalizeWords = (value) =>
    String(value || "")
        .toLowerCase()
        .replace(/\b\w/g, (ch) => ch.toUpperCase());

const getDirectorName = (user) => {
    if (!user) return "Unknown";
    const nickname = user.nickname && user.nickname.trim() !== "-" ? user.nickname.trim() : "";
    return [capitalizeWords(nickname), capitalizeWords(user.name)].filter(Boolean).join(" ");
};

const STATUS_ORDER = { current: 0, upcoming: 1, past: 2 };

const buildDirectorRows = (entries) => {
    const today = moment().startOf("day");
    return (entries || [])
        .map((entry) => {
            const start = parseDirectorDate(entry.startDate);
            const end = parseDirectorDate(entry.endDate);
            let status = "current";
            if (start && start.isAfter(today)) status = "upcoming";
            else if (end && end.isBefore(today)) status = "past";
            return { ...entry, start, end, status };
        })
        .sort((a, b) => {
            if (STATUS_ORDER[a.status] !== STATUS_ORDER[b.status]) {
                return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
            }
            return (b.start ? b.start.valueOf() : 0) - (a.start ? a.start.valueOf() : 0);
        });
};

const STATUS_LABELS = { current: "Current", upcoming: "Upcoming", past: "Past" };

const getInitials = (name) =>
    String(name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join("") || "?";

const formatCompletionDate = (value) => {
    if (!value) return "";
    const parsed = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY"], true);
    return parsed.isValid() ? parsed.format("DD MMM YYYY") : value;
};

const InfoTile = ({ icon: Icon, label, value, wide }) => (
    <div className={`projectinfo-tile ${wide ? "projectinfo-tile-wide" : ""}`}>
        <div className="projectinfo-tile-icon">
            <Icon />
        </div>
        <div className="projectinfo-tile-body">
            <div className="projectinfo-tile-label">{label}</div>
            <div className={`projectinfo-tile-value ${value ? "" : "is-empty"}`}>
                {value || "Not available"}
            </div>
        </div>
    </div>
);

const splitLines = (text) =>
    String(text || "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

const Accordion = ({ id, title, icon: Icon, activeMenu, setActiveMenu, children }) => {
    const open = activeMenu === id;
    return (
        <div className={`projectinfo-accordion ${open ? "is-open" : ""}`}>
            <button
                type="button"
                className={`projectinfo-menu ${open ? "active" : ""}`}
                onClick={() => setActiveMenu(open ? "" : id)}
                aria-expanded={open}
            >
                <span className="projectinfo-menu-icon">
                    <Icon />
                </span>
                <span className="projectinfo-menu-title">{title}</span>
                <span className="projectinfo-menu-chevron">
                    {open ? <FaChevronUp /> : <FaChevronDown />}
                </span>
            </button>
            {open && <div className="projectinfo-content projectinfo-panel">{children}</div>}
        </div>
    );
};

const EmptyState = ({ text }) => <div className="projectinfo-team-empty">{text}</div>;

const ItemList = ({ items, icon: Icon, emptyText }) =>
    items.length === 0 ? (
        <EmptyState text={emptyText} />
    ) : (
        <ul className="projectinfo-items">
            {items.map((item, index) => (
                <li key={index} className="projectinfo-item">
                    <span className={`projectinfo-item-marker ${Icon ? "has-icon" : ""}`}>
                        {Icon && <Icon />}
                    </span>
                    <span className="projectinfo-item-text">{item}</span>
                </li>
            ))}
        </ul>
    );

const DetailGroup = ({ title, icon: Icon, text }) => (
    <div className="projectinfo-team-card projectinfo-group">
        <div className="projectinfo-team-title">
            <span className="projectinfo-team-title-icon">
                <Icon />
            </span>
            <span className="projectinfo-team-title-text">{title}</span>
        </div>
        <ItemList items={splitLines(text)} emptyText="No details available" />
    </div>
);

const DirectorList = ({ title, icon: Icon, entries, emptyText }) => {
    const rows = buildDirectorRows(entries);
    return (
        <div className="projectinfo-team-card">
            <div className="projectinfo-team-title">
                <span className="projectinfo-team-title-icon">
                    <Icon />
                </span>
                <span className="projectinfo-team-title-text">{title}</span>
                <span className="projectinfo-team-count">{rows.length}</span>
            </div>
            {rows.length === 0 ? (
                <div className="projectinfo-team-empty">{emptyText}</div>
            ) : (
                <div className="projectinfo-director-list">
                    {rows.map((row) => {
                        const name = getDirectorName(row.user);
                        return (
                            <div
                                key={row._id}
                                className={`projectinfo-director projectinfo-director-${row.status}`}
                            >
                                <div className="projectinfo-avatar">{getInitials(name)}</div>
                                <div className="projectinfo-director-main">
                                    <div className="projectinfo-director-name">{name}</div>
                                    <div className="projectinfo-director-period">
                                        <FiCalendar />
                                        <span>
                                            {row.start ? row.start.format("DD MMM YYYY") : "—"}
                                            {" – "}
                                            {row.end ? row.end.format("DD MMM YYYY") : "Present"}
                                        </span>
                                    </div>
                                </div>
                                <span
                                    className={`projectinfo-director-badge projectinfo-director-badge-${row.status}`}
                                >
                                    {STATUS_LABELS[row.status]}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const ProjectInfo = withRouter(() => {
    const locate = useLocation();
    const history = useHistory();
    const path = locate.pathname.split("/")[2];

    const [packages, setPackages] = useState(undefined);
    const [activeMenu, setActiveMenu] = useState("");

    useQuery(queries.FETCH_PROJECT_INFO, {
        variables: {
            project: path ? path : "",
        },
        fetchPolicy: "network-only",
        onCompleted: ({ fetchProjectsAgent }) => {
            setPackages(fetchProjectsAgent.data);
        },
        onError: ({ graphQLErrors, networkError }) => {
            if (graphQLErrors) {
                for (const graphQLError of graphQLErrors) {
                    console.log(graphQLError);
                }
            }

            if (networkError) {
                console.log(networkError);
            }
        },
    });

    const handleBackToProjects = () => {
        history.push("/projects");
    };

    return (
        <div>
            {packages && packages[0] && (
                <div className="p-10 lg:px-0 lg:p-0 px-3">
                    <div className="projectinfo-breadcrumb flex items-center gap-2 mb-4">
                        <button
                            onClick={handleBackToProjects}
                            className="flex items-center gap-2 px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors duration-200 text-sm font-medium"
                        >
                            <FaArrowLeft className="text-xs" />
                            Back to Projects
                        </button>
                        <span>PROJECTS</span>
                        <span className="separator">{">"}</span>
                        <span>{packages[0].name}</span>
                    </div>
                    <div className="projectinfo-container">
                        <img
                            src={packages[0].image}
                            alt={packages[0].name}
                            className="projectinfo-image"
                        />
                        <div className="projectinfo-details">
                            <Accordion
                                id="ProjectInfo"
                                title="Project Info"
                                icon={FiInfo}
                                activeMenu={activeMenu}
                                setActiveMenu={setActiveMenu}
                            >
                                    <div className="projectinfo-tiles">
                                        <InfoTile
                                            wide
                                            icon={FiMapPin}
                                            label="Location"
                                            value={packages[0].info.projectLocation}
                                        />
                                        <InfoTile
                                            icon={FiBriefcase}
                                            label="Developer"
                                            value={packages[0].developer}
                                        />
                                        <InfoTile
                                            icon={FiFileText}
                                            label="Land Title"
                                            value={capitalizeWords(packages[0].hold)}
                                        />
                                        <InfoTile
                                            icon={FiMaximize2}
                                            label="Land Size"
                                            value={
                                                packages[0].minSqft || packages[0].maxSqft
                                                    ? `${packages[0].minSqft ?? "—"} – ${packages[0].maxSqft ?? "—"} sqft`
                                                    : ""
                                            }
                                        />
                                        <InfoTile
                                            icon={FiCalendar}
                                            label="Completion Date"
                                            value={formatCompletionDate(packages[0].completionDate)}
                                        />
                                    </div>

                                    <div className="projectinfo-team-heading">
                                        <FiUsers />
                                        <span>Team in Charge</span>
                                    </div>
                                    <div className="projectinfo-team">
                                        <DirectorList
                                            title="Sales Directors"
                                            icon={FiTrendingUp}
                                            entries={packages[0].sdList}
                                            emptyText="No sales director assigned"
                                        />
                                        <DirectorList
                                            title="Project Directors"
                                            icon={FiClipboard}
                                            entries={packages[0].pdList}
                                            emptyText="No project director assigned"
                                        />
                                    </div>
                                    <div className="projectinfo-coordinator">
                                        <div className="projectinfo-avatar projectinfo-avatar-muted">
                                            <FiUser />
                                        </div>
                                        <div className="projectinfo-director-main">
                                            <div className="projectinfo-tile-label">Coordinator</div>
                                            <div
                                                className={`projectinfo-director-name ${
                                                    packages[0].coordinatorName ? "" : "is-empty"
                                                }`}
                                            >
                                                {packages[0].coordinatorName || "Not assigned"}
                                            </div>
                                        </div>
                                    </div>
                            </Accordion>

                            {activeMenu === "downloadKits" && (
                                <div className="projectinfo-content">
                                    {packages[0].info.projectGallery &&
                                        packages[0].info.projectGallery.length > 0 &&
                                        packages[0].info.projectGallery.map((galleryItem) => (
                                            <div
                                                key={galleryItem._id}
                                                className="projectinfo-submenu"
                                                onClick={() => window.open(galleryItem.url, "_blank")}
                                            >
                                                {galleryItem.title}
                                            </div>
                                        ))}
                                </div>
                            )}

                            <Accordion
                                id="projectDetails"
                                title="Project Details"
                                icon={FiLayers}
                                activeMenu={activeMenu}
                                setActiveMenu={setActiveMenu}
                            >
                                <div className="projectinfo-groups">
                                    <DetailGroup
                                        title="Total Blocks - Storey"
                                        icon={FiLayers}
                                        text={packages[0].info.projectBlocks}
                                    />
                                    <DetailGroup
                                        title="Unit Details"
                                        icon={FiGrid}
                                        text={packages[0].info.projectUnits}
                                    />
                                    <DetailGroup
                                        title="Build Up - Layout - Car Park - SPA Price"
                                        icon={FiHome}
                                        text={packages[0].info.projectType}
                                    />
                                </div>
                            </Accordion>

                            <Accordion
                                id="salesPackage"
                                title="Sales Package"
                                icon={FiGift}
                                activeMenu={activeMenu}
                                setActiveMenu={setActiveMenu}
                            >
                                <ItemList
                                    items={splitLines(packages[0].info.projectSalesPackage)}
                                    icon={FiCheck}
                                    emptyText="No sales package available"
                                />
                            </Accordion>

                            <Accordion
                                id="commission"
                                title="Project Commission Percentage"
                                icon={FiPercent}
                                activeMenu={activeMenu}
                                setActiveMenu={setActiveMenu}
                            >
                                <ItemList
                                    items={splitLines(packages[0].info.projectCommPercentage)}
                                    icon={FiPercent}
                                    emptyText="No commission details available"
                                />
                            </Accordion>

                            <Accordion
                                id="remarks"
                                title="Remarks"
                                icon={FiMessageSquare}
                                activeMenu={activeMenu}
                                setActiveMenu={setActiveMenu}
                            >
                                {packages[0].attributes && packages[0].attributes.length > 0 ? (
                                    <div className="projectinfo-tiles">
                                        {packages[0].attributes.map((item, index) => (
                                            <div key={index} className="projectinfo-tile">
                                                <div className="projectinfo-tile-icon">
                                                    <FiMessageSquare />
                                                </div>
                                                <div className="projectinfo-tile-body">
                                                    <div className="projectinfo-tile-label">
                                                        {item.attribute}
                                                    </div>
                                                    <div
                                                        className={`projectinfo-tile-value ${
                                                            item.value ? "" : "is-empty"
                                                        }`}
                                                    >
                                                        {item.value || "Not available"}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <EmptyState text="No remarks" />
                                )}
                            </Accordion>

                            <div className="projectinfo-footer">
                                {packages[0].itinerary && (
                                    <button
                                        onClick={() => window.open(packages[0].itinerary, "_blank")}
                                        className="projectinfo-link-btn itinerary"
                                    >
                                        Itinerary
                                    </button>
                                )}
                                {packages[0].video && (
                                    <button
                                        onClick={() => window.open(packages[0].video, "_blank")}
                                        className="projectinfo-link-btn video"
                                    >
                                        Video
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default ProjectInfo;
